import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuArrowLeft, LuCheck, LuInfo, LuReceipt } from 'react-icons/lu'
import { CurrencySelect } from '@/frontend/components/marina/CurrencySelect'
import { CurrencyText } from '@/frontend/components/marina/CurrencyText'
import { KindBadge } from '@/frontend/components/marina/KindBadge'
import { type LineItem, LineItemEditor } from '@/frontend/components/marina/LineItemEditor'
import { useBoats } from '@/frontend/hooks/useBoats'
import { useCustomers } from '@/frontend/hooks/useCustomers'
import { useCreateInvoice } from '@/frontend/hooks/useInvoices'
import type { CurrencyCode } from '@/frontend/lib/currency'

const FT_PER_METER = 3.28084

const BOAT_TYPE_ID: Record<string, string> = {
  POWERBOAT: 'Powerboat',
  SAILING_BOAT: 'Kapal Layar',
  YACHT: 'Yacht',
  CATAMARAN: 'Katamaran',
  FISHING: 'Kapal Nelayan',
  DINGHY: 'Dinghy',
  OTHER: 'Lainnya',
}

const DUE_PRESETS = [
  { label: '+7 hari', days: 7 },
  { label: '+14 hari', days: 14 },
  { label: '+30 hari', days: 30 },
  { label: '+60 hari', days: 60 },
]

export const Route = createFileRoute('/dashboard/billing/invoices/new')({
  component: NewInvoice,
})

function NewInvoice() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const create = useCreateInvoice()

  // Step 1 — Pelanggan & Kapal
  const [customerId, setCustomerId] = useState<string | null>(null)
  const [boatId, setBoatId] = useState<string | null>(null)

  // Step 2 — Detail Faktur
  const [dueDate, setDueDate] = useState(dayjs().add(14, 'day').format('YYYY-MM-DD'))
  const [taxRate, setTaxRate] = useState(11)
  const [currency, setCurrency] = useState<CurrencyCode>('IDR')
  const [notes, setNotes] = useState('')

  // Step 3 — Penerima Tagihan
  const [useCustomerAsRecipient, setUseCustomerAsRecipient] = useState(true)
  const [recipientName, setRecipientName] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientAddress, setRecipientAddress] = useState('')
  const [recipientTaxId, setRecipientTaxId] = useState('')
  const [recipientCompanyName, setRecipientCompanyName] = useState('')

  // Step 4 — Item Tagihan
  const [items, setItems] = useState<LineItem[]>([{ description: 'Layanan marina', quantity: 1, unitPriceIdr: 0 }])

  const { data: customersData } = useCustomers({ pageSize: 200 })
  const customers = customersData?.items ?? []
  const selectedCustomer = useMemo(() => customers.find((c) => c.id === customerId), [customers, customerId])

  const { data: boatsData } = useBoats({ customerId: customerId ?? undefined, pageSize: 100 })
  const boats = boatsData?.items ?? []
  const selectedBoat = useMemo(() => boats.find((b) => b.id === boatId), [boats, boatId])

  const customerOptions = useMemo(
    () =>
      customers.map((c) => ({
        value: c.id,
        label: `${c.firstName} ${c.lastName}${c.email ? ` · ${c.email}` : ''}`,
      })),
    [customers],
  )
  const boatOptions = useMemo(() => boats.map((b) => ({ value: b.id, label: b.name })), [boats])

  // Auto-fill recipient from customer
  useEffect(() => {
    if (useCustomerAsRecipient && selectedCustomer) {
      setRecipientName(`${selectedCustomer.firstName} ${selectedCustomer.lastName}`)
      setRecipientEmail(selectedCustomer.email ?? '')
      setRecipientAddress(selectedCustomer.billingAddress ?? selectedCustomer.address ?? '')
      setRecipientTaxId(selectedCustomer.taxId ?? '')
      setRecipientCompanyName(selectedCustomer.companyName ?? '')
    }
  }, [useCustomerAsRecipient, selectedCustomer])

  const subtotal = items.reduce((s, it) => s + it.quantity * it.unitPriceIdr, 0)
  const tax = (subtotal * taxRate) / 100
  const total = subtotal + tax

  const daysUntilDue = dueDate ? dayjs(dueDate).diff(dayjs(), 'day') : null
  const canSave = !!customerId && !!dueDate && !!recipientName && items.every((i) => i.description) && subtotal > 0

  async function submit() {
    if (!canSave) return
    try {
      const { invoice } = await create.mutateAsync({
        customerId: customerId!,
        boatId,
        dueDate,
        taxRate,
        currency,
        notes: notes || null,
        recipientName: recipientName || null,
        recipientEmail: recipientEmail || null,
        recipientAddress: recipientAddress || null,
        recipientTaxId: recipientTaxId || null,
        recipientCompanyName: recipientCompanyName || null,
        lineItems: items.map((it) => ({
          description: it.description,
          quantity: it.quantity,
          unitPriceIdr: it.unitPriceIdr,
        })),
      })
      notifications.show({
        title: t('page.billing.invoice_new.notify_created'),
        message: t('page.billing.invoice_new.notify_proforma', { code: invoice.code }),
        color: 'teal',
      })
      navigate({ to: '/dashboard/billing/invoices' })
    } catch (e: unknown) {
      notifications.show({
        title: 'Gagal',
        message: e instanceof Error ? e.message : 'Coba lagi',
        color: 'red',
      })
    }
  }

  return (
    <Container size="md" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group gap="sm" align="center">
          <Anchor component={Link} to="/dashboard/billing/invoices" c="dimmed" style={{ display: 'flex' }}>
            <LuArrowLeft size={18} />
          </Anchor>
          <ThemeIcon variant="light" color="laut" size="lg" radius="md">
            <LuReceipt size={18} />
          </ThemeIcon>
          <div>
            <Title order={2} ff="var(--font-display)" fw={600}>
              {t('page.billing.invoice_new.title')}
            </Title>
            <Text size="xs" c="dimmed">
              {t('page.billing.invoice_new.subtitle')}
            </Text>
          </div>
        </Group>

        {/* PRO_FORMA notice */}
        <Box
          px="md"
          py="xs"
          style={{
            borderRadius: 8,
            background: 'light-dark(var(--mantine-color-blue-0), rgba(34,139,230,0.1))',
          }}
        >
          <Group gap="xs">
            <LuInfo size={14} color="var(--mantine-color-blue-6)" />
            <Text size="xs" c="blue.7">
              {t('page.billing.invoice_new.proforma_notice')}
            </Text>
          </Group>
        </Box>

        {/* Step 1 — Pelanggan */}
        <StepCard
          step={1}
          title={t('page.billing.invoice_new.step_customer')}
          done={!!customerId}
          required
          requiredLabel={t('page.billing.quote_new.required_badge')}
          doneLabel={t('page.billing.quote_new.done_badge')}
        >
          <Select
            label={t('page.billing.invoice_new.label_customer')}
            required
            placeholder={t('page.billing.invoice_new.placeholder_customer')}
            searchable
            clearable
            data={customerOptions}
            value={customerId}
            onChange={(v) => {
              setCustomerId(v)
              setBoatId(null)
            }}
          />
          {selectedCustomer && (
            <Box
              mt="xs"
              p="sm"
              style={{ borderRadius: 8, background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.12))' }}
            >
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon variant="light" color="laut" size="sm" radius="xl">
                  <LuCheck size={12} />
                </ThemeIcon>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Text size="sm" fw={600}>
                    {selectedCustomer.firstName} {selectedCustomer.lastName}
                  </Text>
                  <Group gap="xs" mt={2} wrap="wrap">
                    <KindBadge category={selectedCustomer.category} />
                    {selectedCustomer.email && (
                      <Text size="xs" c="dimmed" truncate>
                        {selectedCustomer.email}
                      </Text>
                    )}
                    {selectedCustomer.phone && (
                      <Text size="xs" c="dimmed">
                        {selectedCustomer.phone}
                      </Text>
                    )}
                    {selectedCustomer._count && (
                      <Text size="xs" c="dimmed">
                        {selectedCustomer._count.invoices} faktur · {selectedCustomer._count.bookings} booking
                      </Text>
                    )}
                  </Group>
                </div>
              </Group>
            </Box>
          )}
          {!customerId && (
            <Text size="xs" c="dimmed" mt={4}>
              Belum terdaftar?{' '}
              <Anchor component={Link} to="/dashboard/accounts/customers/new" c="laut" size="xs">
                {t('page.billing.quote_new.new_customer')}
              </Anchor>
            </Text>
          )}
        </StepCard>

        {/* Step 2 — Kapal (opsional) */}
        <StepCard
          step={2}
          title={t('page.billing.invoice_new.step_boat')}
          done={!!boatId}
          badge={t('page.billing.quote_new.optional_badge')}
          disabled={!customerId}
          doneLabel={t('page.billing.quote_new.done_badge')}
        >
          <Select
            label={t('page.billing.invoice_new.label_boat')}
            placeholder={
              !customerId
                ? t('page.billing.quote_new.placeholder_boat_no_customer')
                : boats.length === 0
                  ? t('page.billing.quote_new.placeholder_boat_empty')
                  : t('page.billing.invoice_new.placeholder_boat')
            }
            searchable
            clearable
            disabled={!customerId || boats.length === 0}
            data={boatOptions}
            value={boatId}
            onChange={setBoatId}
          />
          {selectedBoat && (
            <Box
              mt="xs"
              p="sm"
              style={{ borderRadius: 8, background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.12))' }}
            >
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon variant="light" color="laut" size="sm" radius="xl">
                  <LuCheck size={12} />
                </ThemeIcon>
                <div>
                  <Text size="sm" fw={600}>
                    {selectedBoat.name}
                  </Text>
                  <Group gap="xs" mt={2} wrap="wrap">
                    <Badge size="xs" variant="light" color="blue">
                      {BOAT_TYPE_ID[selectedBoat.type] ?? selectedBoat.type}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      LOA {(Number(selectedBoat.loaMeters) * FT_PER_METER).toFixed(1)} ft · Beam{' '}
                      {(Number(selectedBoat.beamMeters) * FT_PER_METER).toFixed(1)} ft
                    </Text>
                    {selectedBoat.flagState && (
                      <Text size="xs" c="dimmed">
                        {selectedBoat.flagState}
                      </Text>
                    )}
                  </Group>
                </div>
              </Group>
            </Box>
          )}
          {customerId && boats.length === 0 && (
            <Text size="xs" c="dimmed" mt={4}>
              {t('page.billing.quote_new.no_boats')}{' '}
              <Anchor component={Link} to={`/dashboard/accounts/customers/${customerId}` as any} c="laut" size="xs">
                {t('page.billing.quote_new.add_boat')}
              </Anchor>
            </Text>
          )}
        </StepCard>

        {/* Step 3 — Detail Faktur */}
        <StepCard
          step={3}
          title={t('page.billing.invoice_new.step_details')}
          done
          doneLabel={t('page.billing.quote_new.done_badge')}
        >
          <Stack gap="md">
            <div>
              <Text size="sm" fw={500} mb={6}>
                {t('page.billing.invoice_new.due_date')} <span style={{ color: 'var(--mantine-color-red-6)' }}>*</span>
              </Text>
              <Group gap="xs" mb={8} wrap="wrap">
                {DUE_PRESETS.map((p) => (
                  <Button
                    key={p.days}
                    size="xs"
                    variant={dueDate === dayjs().add(p.days, 'day').format('YYYY-MM-DD') ? 'filled' : 'light'}
                    color="laut"
                    onClick={() => setDueDate(dayjs().add(p.days, 'day').format('YYYY-MM-DD'))}
                  >
                    {p.label}
                  </Button>
                ))}
              </Group>
              <TextInput type="date" value={dueDate} onChange={(e) => setDueDate(e.currentTarget.value)} maw={220} />
              {daysUntilDue !== null && dueDate && (
                <Text size="xs" c={daysUntilDue < 7 ? 'orange.6' : daysUntilDue < 0 ? 'red.6' : 'dimmed'} mt={4}>
                  {daysUntilDue > 0
                    ? t('page.billing.invoice_new.due_days_left', {
                        n: daysUntilDue,
                        date: dayjs(dueDate).format('DD MMM YYYY'),
                      })
                    : daysUntilDue === 0
                      ? t('page.billing.invoice_new.due_today', { date: dayjs(dueDate).format('DD MMM YYYY') })
                      : t('page.billing.invoice_new.due_passed', { date: dayjs(dueDate).format('DD MMM YYYY') })}
                </Text>
              )}
            </div>

            <div>
              <Text size="sm" fw={500} mb={6}>
                {t('page.billing.invoice_new.tax_rate')}
              </Text>
              <Group gap="xs" mb={8}>
                <Button
                  size="xs"
                  variant={taxRate === 0 ? 'filled' : 'light'}
                  color="laut"
                  onClick={() => setTaxRate(0)}
                >
                  {t('page.billing.invoice_new.tax_free')}
                </Button>
                <Button
                  size="xs"
                  variant={taxRate === 11 ? 'filled' : 'light'}
                  color="laut"
                  onClick={() => setTaxRate(11)}
                >
                  {t('page.billing.invoice_new.tax_ppn')}
                </Button>
              </Group>
              <Group gap="sm" align="flex-end">
                <NumberInput
                  placeholder="Custom %"
                  value={taxRate}
                  onChange={(v) => setTaxRate(typeof v === 'number' ? v : 0)}
                  min={0}
                  max={100}
                  suffix="%"
                  w={140}
                />
                <CurrencySelect
                  label={t('common.currency', { defaultValue: 'Currency' })}
                  value={currency}
                  onChange={setCurrency}
                  w={140}
                />
              </Group>
            </div>

            <Textarea
              label={t('page.billing.invoice_new.notes_label')}
              description={t('page.billing.invoice_new.notes_desc')}
              placeholder={t('page.billing.invoice_new.notes_placeholder')}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
            />
          </Stack>
        </StepCard>

        {/* Step 4 — Penerima Tagihan */}
        <StepCard
          step={4}
          title={t('page.billing.invoice_new.step_recipient')}
          done={!!recipientName}
          disabled={!customerId}
          doneLabel={t('page.billing.quote_new.done_badge')}
        >
          <Stack gap="md">
            <Switch
              label={t('page.billing.invoice_new.recipient_same')}
              description={t('page.billing.invoice_new.recipient_same_desc')}
              checked={useCustomerAsRecipient}
              onChange={(e) => setUseCustomerAsRecipient(e.currentTarget.checked)}
            />

            {useCustomerAsRecipient && recipientName ? (
              <Box
                p="sm"
                style={{
                  borderRadius: 8,
                  background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.12))',
                }}
              >
                <Group gap="sm" wrap="nowrap" align="flex-start">
                  <ThemeIcon variant="light" color="laut" size="sm" radius="xl" mt={2}>
                    <LuCheck size={12} />
                  </ThemeIcon>
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={600}>
                      {recipientName}
                    </Text>
                    {recipientCompanyName && (
                      <Text size="xs" c="dimmed">
                        {recipientCompanyName}
                      </Text>
                    )}
                    {recipientEmail && (
                      <Text size="xs" c="dimmed">
                        {recipientEmail}
                      </Text>
                    )}
                    {recipientTaxId && (
                      <Text size="xs" c="dimmed">
                        NPWP: {recipientTaxId}
                      </Text>
                    )}
                    {recipientAddress && (
                      <Text size="xs" c="dimmed">
                        {recipientAddress}
                      </Text>
                    )}
                  </Stack>
                </Group>
              </Box>
            ) : useCustomerAsRecipient && !recipientName ? (
              <Text size="xs" c="dimmed">
                Pilih pelanggan dulu untuk melihat data penerima.
              </Text>
            ) : (
              <Stack gap="sm">
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <TextInput
                    label={t('page.billing.invoice_new.field_name')}
                    required
                    placeholder="Nama penerima faktur"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.currentTarget.value)}
                  />
                  <TextInput
                    label={t('page.billing.invoice_new.field_email')}
                    type="email"
                    placeholder="email@domain.com"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.currentTarget.value)}
                  />
                  <TextInput
                    label={t('page.billing.invoice_new.field_company')}
                    placeholder="PT / CV / UD (opsional)"
                    value={recipientCompanyName}
                    onChange={(e) => setRecipientCompanyName(e.currentTarget.value)}
                  />
                  <TextInput
                    label={t('page.billing.invoice_new.field_npwp')}
                    placeholder="00.000.000.0-000.000"
                    value={recipientTaxId}
                    onChange={(e) => setRecipientTaxId(e.currentTarget.value)}
                  />
                </SimpleGrid>
                <Textarea
                  label={t('page.billing.invoice_new.field_address')}
                  placeholder="Jl. ..."
                  rows={2}
                  value={recipientAddress}
                  onChange={(e) => setRecipientAddress(e.currentTarget.value)}
                />
              </Stack>
            )}
          </Stack>
        </StepCard>

        {/* Step 5 — Item Tagihan */}
        <StepCard
          step={5}
          title={t('page.billing.invoice_new.step_items')}
          done={subtotal > 0}
          required
          requiredLabel={t('page.billing.quote_new.required_badge')}
          doneLabel={t('page.billing.quote_new.done_badge')}
        >
          <LineItemEditor items={items} onChange={setItems} taxRate={taxRate} onTaxRateChange={setTaxRate} />
          {subtotal <= 0 && (
            <Text size="xs" c="dimmed" mt="xs">
              {t('page.billing.invoice_new.items_hint')}
            </Text>
          )}
        </StepCard>

        {/* Ringkasan + CTA */}
        <Card
          radius="md"
          padding="lg"
          style={{ background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.08))' }}
        >
          <Stack gap="sm">
            <Text fw={600} size="sm" c="laut.8">
              {t('page.billing.invoice_new.summary_title')}
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xs">
              <SummaryField
                label={t('page.billing.invoice_new.field_customer')}
                value={
                  selectedCustomer
                    ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
                    : t('page.billing.invoice_new.no_customer')
                }
              />
              <SummaryField label={t('page.billing.invoice_new.field_boat')} value={selectedBoat?.name ?? '—'} />
              <SummaryField label={t('page.billing.invoice_new.field_recipient')} value={recipientName || '—'} />
              <SummaryField
                label={t('page.billing.invoice_new.field_due')}
                value={dueDate ? dayjs(dueDate).format('DD MMM YYYY') : '—'}
              />
            </SimpleGrid>
            <Divider />
            <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
              <Group gap="xl">
                <div>
                  <Text size="xs" c="dimmed">
                    {t('page.billing.invoice_new.subtotal')}
                  </Text>
                  <CurrencyText idr={subtotal} size="sm" />
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    {t('page.billing.invoice_new.tax', { rate: taxRate })}
                  </Text>
                  <CurrencyText idr={tax} size="sm" />
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    {t('common.total')}
                  </Text>
                  <CurrencyText idr={total} fw={700} size="lg" c="laut.7" />
                </div>
              </Group>
              <Group gap="sm">
                <Button variant="default" onClick={() => navigate({ to: '/dashboard/billing/invoices' })}>
                  {t('page.billing.invoice_new.btn_cancel')}
                </Button>
                <Button color="karang" disabled={!canSave} loading={create.isPending} onClick={submit}>
                  {t('page.billing.invoice_new.btn_create')}
                </Button>
              </Group>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  )
}

function StepCard({
  step,
  title,
  badge,
  done,
  disabled,
  required,
  requiredLabel = 'Wajib',
  doneLabel = 'Selesai',
  children,
}: {
  step: number
  title: string
  badge?: string
  done: boolean
  disabled?: boolean
  required?: boolean
  requiredLabel?: string
  doneLabel?: string
  children: React.ReactNode
}) {
  return (
    <Card radius="md" padding="lg" style={{ opacity: disabled ? 0.5 : 1, transition: 'opacity 200ms' }}>
      <Group gap="sm" mb="md" align="center">
        <ThemeIcon
          size={26}
          radius="xl"
          color={done ? 'laut' : 'gray'}
          variant={done ? 'filled' : 'light'}
          style={{ flexShrink: 0 }}
        >
          {done ? (
            <LuCheck size={13} />
          ) : (
            <Text size="xs" fw={700}>
              {step}
            </Text>
          )}
        </ThemeIcon>
        <Text fw={600} size="sm">
          {title}
        </Text>
        {required && !done && (
          <Badge size="xs" color="red" variant="light">
            {requiredLabel}
          </Badge>
        )}
        {badge && !done && (
          <Badge size="xs" color="gray" variant="light">
            {badge}
          </Badge>
        )}
        {done && (
          <Badge size="xs" color="teal" variant="light">
            {doneLabel}
          </Badge>
        )}
      </Group>
      <div style={{ pointerEvents: disabled ? 'none' : undefined }}>{children}</div>
    </Card>
  )
}

function SummaryField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={500} truncate>
        {value}
      </Text>
    </div>
  )
}
